/**
 * What a BROWSER sees when it navigates to a BrowserDAV share.
 *
 * The protocol core answers WebDAV clients; these builders answer a person
 * with a tab open. Ported from the BrowserDAV app's main.js when the engine
 * moved into acequia.js (browserdav-capability project, 2026-09-20) — the
 * app cannot be a thin shell while it still renders half the responses, and
 * these are parity features (browserdav-nephele-parity: `?acequia-html`,
 * `?acequia-editor`, `?acequia-dir`, the file browser page) that belong to
 * the server, whichever process runs it.
 *
 * Pure: every builder takes what it needs and returns a response envelope
 * `{ status, headers, body }`. No `this`, no globals, so a test can call them
 * directly and the server can wire them without ceremony.
 *
 * The pages reference `/acequia.js` and `/components/…` on the ORIGIN the
 * browser fetched them from — the domain server's component set. That is the
 * one assumption: a BrowserDAV share is reached through an Acequia domain's
 * group proxy, so those paths resolve.
 */

export function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

export function formatSize(bytes) {
    if (bytes == null) return '-'
    if (bytes < 1024) return `${bytes} B`
    const units = ['KB', 'MB', 'GB', 'TB']
    let v = bytes / 1024
    let i = 0
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024
        i++
    }
    return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`
}

/**
 * Is this request a person navigating in a browser, as opposed to a client
 * that asked for a listing by parameter or header? `Accept: text/html` with
 * none of the acequia-* switches present.
 */
export function isBrowserNavigation(data) {
    const method = (data.method || 'GET').toUpperCase()
    if (method !== 'GET') return false
    const accept = data.headers?.accept || data.headers?.Accept || ''
    if (!accept.includes('text/html')) return false
    const q = data.query || {}
    const hasParam = 'acequia-json' in q || 'acequia-html' in q || 'acequia-editor' in q
    const hasHeader = !!(data.headers?.['x-acequia-directory-json'] || data.headers?.['x-directory-as-json'])
    return !hasParam && !hasHeader
}

/** A directory request, by shape: trailing slash, or a last segment with no dot. */
export function looksLikeDirectory(reqPath) {
    return !reqPath || reqPath.endsWith('/') || !reqPath.split('/').pop()?.includes('.')
}

/**
 * The file-browser page: <acequia-file-browser> over this share, with the
 * editor wired in. `token` (if the viewer presented one) rides into the
 * element so the browser authenticates as its holder; a viewer with none
 * gets the wallet-or-prompt behaviour the element implements.
 *
 * @param {object} p
 * @param {string} p.dirPath      share-relative directory ('/' for the root)
 * @param {string} p.dirName      title
 * @param {string|null} p.token
 * @param {string} p.rootUrl      absolute client-visible base of the instance
 * @param {string} p.basePath     the path-mode base the element navigates within
 */
export function fileBrowserPage({ dirPath, dirName, token, rootUrl, basePath }) {
    const esc = escapeHtml
    return {
        status: 200,
        headers: {
            'Content-Type': 'text/html; charset=utf-8',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
        body: `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${esc(dirName)} — BrowserDAV</title>
  <style>
    html,body{height:100%;margin:0;background:#1a1a2e;color:#e0e0e0;font-family:system-ui}
    body{display:flex;flex-direction:column}
    acequia-file-browser{flex:1}
    #editorWrapper{display:none;flex-direction:column;flex:1}
    #editorWrapper.active{display:flex}
    #editorWrapper .back-bar{display:flex;align-items:center;gap:8px;padding:8px 12px;background:#1a1a2e;border-bottom:1px solid #333}
    #editorWrapper .back-btn{background:none;border:1px solid #555;color:#e0e0e0;padding:4px 12px;border-radius:4px;cursor:pointer;font-size:0.85rem}
    #editorWrapper .back-btn:hover{background:#333}
    #editorWrapper .file-path{font-size:0.8rem;color:#888;font-family:monospace}
    acequia-editor{flex:1}
  </style>
</head>
<body>
  <acequia-file-browser id="fb"
    root-url="${esc(rootUrl)}"
    ${token ? `auth-token="${esc(token)}"` : ''}
    initial-path="${esc(dirPath)}"
    url-mode="path"
    base-path="${esc(basePath)}"
    ${token ? 'editable' : ''}
    show-access-indicators
  ></acequia-file-browser>
  <div id="editorWrapper">
    <div class="back-bar">
      <button class="back-btn" onclick="closeEditor()">← Back to Files</button>
      <span class="file-path" id="editorPath"></span>
    </div>
    <acequia-editor id="editor" ${token ? `auth-token="${esc(token)}"` : ''}></acequia-editor>
  </div>
  <!-- acequia.js exposes the IndexedDB token wallet on this origin. With it
       loaded, a visitor who already holds tokens (the owner, on their own
       machine) authenticates without pasting anything; a visitor who holds
       none has an empty wallet and is prompted as before. The credential
       always comes from the viewer's own storage, never from this server. -->
  <script type="module" src="/acequia.js"></script>
  <script type="module" src="/components/acequia-file-browser/acequia-file-browser.js"></script>
  <script type="module" src="/components/acequia-editor/acequia-editor.js"></script>
  <script>
    const fb = document.getElementById('fb')
    const wrapper = document.getElementById('editorWrapper')
    const editor = document.getElementById('editor')
    fb.addEventListener('file-action', e => {
      const { action, entry, url } = e.detail
      if (action === 'edit') {
        document.getElementById('editorPath').textContent = entry.name
        editor.setAttribute('src', url)
        fb.style.display = 'none'
        wrapper.classList.add('active')
        editor.addEventListener('editor-save', () => { if (fb._fileList) fb._fileList.refresh() })
      }
    })
    window.closeEditor = () => {
      fb.style.display = ''
      wrapper.classList.remove('active')
      editor.removeAttribute('src')
    }
  </script>
</body>
</html>`,
    }
}

/**
 * `?acequia-html`: a plain, dependency-free directory index. Links carry the
 * parameter so browsing stays in this mode.
 */
export function plainIndexPage({ pathname, entries }) {
    const parentPath = pathname === '/' ? null : pathname.replace(/[^/]+\/?$/, '')
    const rows = entries
        .map((e) => {
            const icon = e.type === 'directory' ? '&#128193;' : '&#128196;'
            const href = './' + escapeHtml(e.basename) + (e.type === 'directory' ? '/?acequia-html=1' : '')
            const size = e.type === 'directory' ? '-' : formatSize(e.size)
            const date = e.lastmod ? new Date(e.lastmod).toISOString().replace('T', ' ').slice(0, 19) : ''
            return `<tr><td>${icon} <a href="${href}">${escapeHtml(e.basename)}</a></td><td>${size}</td><td>${date}</td></tr>`
        })
        .join('\n')
    const parentRow = parentPath
        ? `<tr><td>&#128193; <a href="../?acequia-html=1">..</a></td><td>-</td><td></td></tr>\n`
        : ''
    return {
        status: 200,
        headers: { 'Content-Type': 'text/html; charset=utf-8', 'Cache-Control': 'private, no-cache' },
        body: `<!doctype html>
<html><head>
<meta charset="utf-8">
<title>Index of ${escapeHtml(pathname)}</title>
<style>
  body { font-family: monospace; margin: 2em; background: #1a1a2e; color: #e0e0e0; }
  h1 { font-size: 1.2em; color: #ccc; }
  table { border-collapse: collapse; width: 100%; }
  th { text-align: left; border-bottom: 1px solid #444; padding: 4px 12px; color: #888; }
  td { padding: 4px 12px; }
  a { color: #7eb8da; text-decoration: none; }
  a:hover { text-decoration: underline; }
  tr:hover { background: rgba(255,255,255,0.03); }
</style>
</head><body>
<h1>Index of ${escapeHtml(pathname)}</h1>
<table>
<tr><th>Name</th><th>Size</th><th>Modified</th></tr>
${parentRow}${rows}
</table>
</body></html>`,
    }
}

/** `?acequia-editor`: <acequia-editor> over one file. */
export function editorPage({ reqPath, fileUrl, token }) {
    return {
        status: 200,
        headers: { 'Content-Type': 'text/html; charset=utf-8', 'Cache-Control': 'no-cache' },
        body: `<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Edit ${escapeHtml(reqPath)} — BrowserDAV</title>
<style>html,body{height:100%;margin:0;background:#1a1a2e;color:#e0e0e0}acequia-editor{display:block;height:100%}</style>
</head><body>
<acequia-editor src="${escapeHtml(fileUrl)}" ${token ? `auth-token="${escapeHtml(token)}"` : ''}></acequia-editor>
<script type="module" src="/components/acequia-editor/acequia-editor.js"><\/script>
</body></html>`,
    }
}
